import apiClient from './apiClient';

/**
 * Get all notifications for current user
 * @returns {Promise} - Array of notifications
 */
export const getAllNotifications = async () => {
  try {
    const response = await apiClient.get('/notifications/list');
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Update FCM device token for push notifications
 * @param {string} token - FCM token from Firebase Cloud Messaging
 * @returns {Promise} - Success response
 */
export const updateFCMToken = async (token) => {
  try {
    const response = await apiClient.post('/notifications/register-device', {
      deviceID: token,
      deviceType: 'web',
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Delete FCM token (usually on logout)
 * @returns {Promise} - Success response
 */
export const deleteFCMToken = async () => {
  try {
    const response = await apiClient.post('/notifications/unregister-device');
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Mark notification as read
 * @param {number} notificationId - Notification ID
 * @returns {Promise} - Success response
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await apiClient.post(
      `/notifications/${notificationId}/mark-read`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Delete notification
 * @param {number} notificationId - Notification ID
 * @returns {Promise} - Success response
 */
export const deleteNotification = async (notificationId) => {
  try {
    const response = await apiClient.delete(`/notifications/${notificationId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Request notification permission from browser
 * @returns {Promise<boolean>} - true if permission granted
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

/**
 * Subscribe to push notifications using Service Worker
 * @param {string} vapidKey - VAPID public key for web push
 * @returns {Promise<string>} - Subscription endpoint token
 */
export const subscribeToPushNotifications = async (vapidKey) => {
  try {
    // Check for service worker and push API support
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      throw new Error('Push notifications not supported in this browser');
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKey,
    });

    return subscription.endpoint;
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    throw error;
  }
};

/**
 * Send test notification (for development)
 * @returns {Promise} - Success response
 */
export const sendTestNotification = async () => {
  try {
    const response = await apiClient.post('/notifications/test');
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};
